import { PaymentAssignmentStatusId } from '../PaymentAssignment/Utils/types'
import {
  STATUS_FIELD_CONFIG,
  STATUSES_WITH_FIELD_CONFIG,
  StatusUpdateFormKey,
  StatusUpdateFormKeyLabels,
} from '../PaymentAssignment/CaseManagement/statusUpdate.type'
import { CLEAR_BY_MANUAL_STATUSES } from '../PaymentAssignment/Utils/rolePolicy'
import useFeatureFlags from 'hooks/useFeatureFlags'

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export const CHUNK_SIZE = 100
export const MAX_CSV_ROWS = 20000
export const ALLOWED_STATUSES: PaymentAssignmentStatusId[] =
  STATUSES_WITH_FIELD_CONFIG

/**
 * Hook version of ALLOWED_STATUSES: filters out the three "clear-by-manual"
 * statuses (AP_CONFIRMED, PAYMENT_CLEARED, BOT_CLEARED) when the flag
 * `admin.finance.payment-coordination-enable-clear-by-manual` is off.
 */
export function useAllowedBulkStatuses(): PaymentAssignmentStatusId[] {
  const { flags } = useFeatureFlags()
  const enableClearByManual =
    flags['admin.finance.payment-coordination-enable-clear-by-manual']
  if (enableClearByManual) return ALLOWED_STATUSES
  return ALLOWED_STATUSES.filter((s) => !CLEAR_BY_MANUAL_STATUSES.has(s))
}

/**
 * ALL field keys that appear across every allowed status — used to build the
 * fixed set of columns in the horizontal excel-style table.
 */
export const ALL_FIELD_KEYS: StatusUpdateFormKey[] = Array.from(
  new Set(
    ALLOWED_STATUSES.flatMap((s) =>
      (STATUS_FIELD_CONFIG[s] ?? []).map((r) => r.key)
    )
  )
)

/**
 * CSV column header for a field key — uses the human-readable label from
 * StatusUpdateFormKeyLabels so the downloaded template and uploaded file both
 * use friendly names (e.g. "Actual Premium Paid") instead of camelCase keys.
 */
export const fieldCsvHeader = (key: StatusUpdateFormKey): string =>
  StatusUpdateFormKeyLabels[key] ?? key

/**
 * Reverse map: CSV header string → StatusUpdateFormKey.
 * Accepts both the human-readable label AND the raw enum value so old files
 * with camelCase headers are still accepted.
 */
export const LABEL_TO_FIELD_KEY: Record<string, StatusUpdateFormKey> =
  ALL_FIELD_KEYS.reduce<Record<string, StatusUpdateFormKey>>((acc, key) => {
    acc[fieldCsvHeader(key).toLowerCase()] = key // label match (primary)
    acc[key.toLowerCase()] = key // raw key match (fallback)
    return acc
  }, {})

/**
 * Returns true if `v` is a valid status string.
 *
 * Pass `allowedStatuses` (from `useAllowedBulkStatuses`) to also enforce the
 * `admin.finance.payment-coordination-enable-clear-by-manual` flag.  When
 * omitted the function falls back to the full static list.
 */
export const isAllowedStatus = (
  v: string,
  allowedStatuses?: ReadonlyArray<PaymentAssignmentStatusId>
): v is PaymentAssignmentStatusId =>
  ((allowedStatuses ?? ALLOWED_STATUSES) as string[]).includes(v)
